import { useEffect, useRef, useState } from 'react';
import {
  createRootRoute,
  Outlet,
  useLocation,
  useRouter,
  useRouterState,
  useNavigate,
  useSearch
} from '@tanstack/react-router';
import { useAuth } from '@/hooks/useAuth';
import { useSitzungsSync } from '@/hooks/useSitzungsSync';
import { PageLayout } from '@/components/layout/PageLayout';
import { Spinner } from '@/components/ui/Spinner';
import { ApiError } from '@/lib/api';
import { merkeSitzungBestand, verlangeReauth } from '@/lib/reauth';
import { AuthStoerung } from '@/components/auth/AuthStoerung';
import { NewBestellungSheet } from '@/components/bestellung/NewBestellungSheet';
import { TourProvider } from '@/onboarding/TourProvider';
import { melde, seitenName } from '@/lib/nutzung';
import { ReserviertBlatt } from '@/components/reserviert/ReserviertBlatt';
import { neueBestellungGespeichert, neueBestellungSchliessen, neueBestellungVergessen } from '@/lib/neueBestellung';

type RootSearch = { neu?: 1; reserviert?: string };

/**
 * Ruhetakt fuer das automatische Nachfragen bei einer Auth-Stoerung.
 *
 * Die Selbstheilung soll bleiben — ein Tablet an der Wand muss ohne
 * Fingertipp zurueckkommen, sobald der Server wieder da ist. Sie darf nur
 * nicht mehr so schnell laufen, wie der Browser hergibt (A-06: 370 bis 750
 * Versuche je Sekunde). Sieben Sekunden liegen in der Mitte des
 * beauftragten Fensters von fuenf bis zehn.
 */
const STOERUNG_NACHFRAGE_MS = 7000;

function Shell() {
  const router = useRouter();
  const pathname = useLocation({ select: (l) => l.pathname });
  const auth = useAuth();
  const pending = useRouterState({ select: (s) => s.isLoading });
  const navigate = useNavigate();
  const search = useSearch({ from: '__root__' }) as RootSearch;

  // Nutzerwechsel gilt geraeteweit — alle Fenster muessen mit (B88).
  useSitzungsSync();

  // Nutzungsmessung: welche Seiten werden auf welchem Gerät genutzt?
  // Ohne Bestellnummer (seitenName), nur angemeldet.
  const angemeldet = auth.data !== undefined;
  // Geht das Fenster „Neue Bestellung" anders zu als über closeSheet —
  // Zurück-Geste, Browser-Taste —, ist sein Verlaufsschritt verbraucht.
  const neuOffen = search.neu === 1;
  useEffect(() => {
    if (!neuOffen) neueBestellungVergessen();
  }, [neuOffen]);

  useEffect(() => {
    if (angemeldet) melde('seite', { ort: seitenName(pathname) });
  }, [pathname, angemeldet]);

  // Hatte dieser Tab jemals eine gueltige Sitzung? Der Unterschied ist
  // entscheidend (Befund 42): "noch nie angemeldet" gehoert auf die
  // Login-Seite, "Sitzung ist mitten in der Arbeit gestorben" gehoert zum
  // ReauthDialog. Frueher liefen beide Wege gleichzeitig los und kamen
  // sich in die Quere — Redirect auf /login und Reauth-Dialog gleichzeitig.
  const hatteSession = useRef(false);
  if (auth.data) {
    hatteSession.current = true;
    // Auch dem fetch-Wrapper mitteilen: erst ab jetzt darf ein 401 den
    // Reauth-Dialog ausloesen.
    merkeSitzungBestand();
  }

  const istAuth401 =
    auth.isError && auth.error instanceof ApiError && auth.error.status === 401;

  // A-06: auth/me ist gescheitert, aber NICHT mit 401.
  //
  // Bis hierher gab es fuer diesen Fall keinen Zweig, und das war kein
  // Schoenheitsfehler, sondern die Endlosschleife selbst. Der Ablauf,
  // nachgemessen im Pruefstand: Die Abfrage scheitert, `isPending` faellt,
  // die Anwendung wird gerendert — und jede Komponente darin, die
  // `useAuth()` ruft (PageLayout ueber useAkzentFarbe, TourProvider,
  // NewBestellungSheet, die Dashboard-Route), meldet beim Einhaengen einen
  // neuen Beobachter an. Der erste Beobachter einer Abfrage loest ein
  // Nachladen aus, und ein Nachladen ohne je geladene Daten setzt den
  // Zustand zurueck auf `pending`. Damit steht wieder das Drehraedchen da,
  // der Baum wird abgeraeumt, die naechste Antwort kommt, und das Ganze
  // von vorn — mehrere hundert Mal je Sekunde.
  //
  // Der Riegel ist deshalb ein GEMERKTER Zustand, kein abgeleiteter: Sonst
  // fiele die Ansicht waehrend jedes laufenden Versuchs auf `isPending`
  // zurueck und riefe damit genau die Schleife wieder auf, die sie
  // verhindern soll. Er faellt erst, wenn die Anmeldung wieder gelingt
  // oder der Server mit einem 401 antwortet — dann sind wieder die
  // Zweige darunter zustaendig.
  //
  // Gesetzt wird er waehrend des Renderns, nicht in einem Effekt. Ein
  // Effekt liefe erst NACH dem Commit — und in diesem einen Commit stuende
  // die volle Anwendung im Baum, haengte ihre Beobachter ein und stiesse
  // die Schleife wieder an. React rendert bei einem setState waehrend des
  // Renderns sofort neu und zeigt den Zwischenstand nie an.
  const [authStoerung, setAuthStoerung] = useState(false);
  if (auth.isError && !istAuth401) {
    if (!authStoerung) setAuthStoerung(true);
  } else if (auth.data || istAuth401) {
    if (authStoerung) setAuthStoerung(false);
  }

  // Die Druckseite für Safari (iPhone-Druck) braucht keine Anmeldung:
  // Berechtigt ist das Zeichen in der Adresse, nicht die Sitzung — Safari
  // hat die Sitzung der Home-Bildschirm-App nicht (routes/drucken.$zeichen).
  const offeneDruckseite = pathname.startsWith('/drucken/');

  const authedButOnLogin = auth.data && pathname === '/login';
  const sessionAbgelaufen = istAuth401 && hatteSession.current;
  const unauthedOffLogin =
    istAuth401 && !hatteSession.current && pathname !== '/login' && !offeneDruckseite;

  // Abgelaufene Sitzung: Ansicht stehen lassen und neu anmelden lassen.
  // Kein Routenwechsel, damit ungespeicherte Eingaben erhalten bleiben.
  // refetch ist identitaetsstabil; auth selbst waere es nicht und wuerde
  // den Effekt bei jedem Render erneut ausloesen.
  const authRefetch = auth.refetch;
  useEffect(() => {
    if (!sessionAbgelaufen) return;
    void verlangeReauth().then((ok) => {
      if (ok) void authRefetch();
    });
  }, [sessionAbgelaufen, authRefetch]);

  // Selbstheilung im Ruhetakt (A-06). Der Sturm von heute heilt sich
  // zuverlaessig selbst — das war seine einzige gute Eigenschaft, und sie
  // soll bleiben: Auf dem Tablet an der Wand tippt niemand einen Knopf,
  // wenn morgens das WLAN spaeter hochkommt als das Geraet. Nur eben alle
  // sieben Sekunden statt hunderte Male je Sekunde.
  useEffect(() => {
    if (!authStoerung) return;
    const takt = window.setInterval(() => {
      void authRefetch();
    }, STOERUNG_NACHFRAGE_MS);
    return () => window.clearInterval(takt);
  }, [authStoerung, authRefetch]);

  useEffect(() => {
    if (authedButOnLogin) {
      void router.navigate({ to: '/' });
    } else if (unauthedOffLogin) {
      // Initialer Aufruf ohne Sitzung: hierher zurueckkehren, nicht
      // pauschal aufs Dashboard (S-1). Abgelaufene Sitzungen mitten in
      // der Arbeit landen dagegen gar nicht mehr hier, die faengt der
      // ReauthDialog ab.
      const ziel = window.location.pathname + window.location.search;
      void router.navigate({
        to: '/login',
        search: { ziel: ziel === '/' ? undefined : ziel } as never
      });
    }
  }, [router, authedButOnLogin, unauthedOffLogin]);

  // Nur der allererste Auth-Check blendet die App aus. Frueher stand hier
  // zusaetzlich der Router-Ladezustand — und weil jede Aenderung der
  // Suchparameter eine Navigation ist, wurde bei jedem getippten Zeichen
  // der gesamte Outlet samt Suchfeld ausgehaengt und neu gemountet. Der
  // Fokus landete dabei auf BODY (Befund 40). Der Ladezustand darf den
  // Baum nicht mehr ersetzen; er wird nur noch als schmaler Balken
  // angezeigt.
  // Die Stoerungsansicht steht VOR dem Ladezweig: waehrend eines laufenden
  // Versuchs ist die Abfrage wieder `pending`, und der Spinner an dieser
  // Stelle waere genau die weisse Flaeche, um die es in A-06 geht.
  //
  // Die Anmeldeseite bleibt ausgenommen. Sie ist die einzige Route, die
  // sich im Befund richtig verhalten hat (eine Anfrage, lesbarer Text),
  // und sie braucht die Anmeldung nicht: ihr Knopf geht als Server-Umleitung
  // zu Nextcloud, unabhaengig davon, was auth/me gerade sagt.
  if (offeneDruckseite) {
    return <Outlet />;
  }

  if (authStoerung && pathname !== '/login') {
    return <AuthStoerung laeuft={auth.isFetching} onErneut={() => void authRefetch()} />;
  }

  if (auth.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (authedButOnLogin || unauthedOffLogin) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (pathname === '/login') {
    return <Outlet />;
  }

  // Die Druckansicht bekommt keinen App-Rahmen: Navigation und Kopfzeile
  // haben auf einem Bestellzettel nichts verloren.
  // Der Glomer-Lieferschein ebenso.
  if (pathname.endsWith('/druck') || pathname.endsWith('/lieferschein')) {
    return <Outlet />;
  }

  const sheetOpen = search.neu === 1;
  // Befund 03.11.2026 — „Zurück" öffnete das Fenster wieder. Schließen
  // verbraucht jetzt den Verlaufsschritt des Fensters, statt einen neuen
  // anzuhängen (lib/neueBestellung.ts).
  function closeSheet(): void {
    neueBestellungSchliessen(router, navigate as never);
  }

  return (
    // Die Tour liegt um die Anwendung, nicht daneben: sie muss Router und
    // Abfragen erreichen, und die Hilfe in der Seitenspalte greift auf
    // dieselbe Steuerung zu.
    <TourProvider>
      {/* Ladehinweis ohne Eingriff in den Baum — er darf den Outlet nicht
          ersetzen, sonst verliert jedes fokussierte Feld den Fokus. */}
      {pending && (
        <div
          role="status"
          aria-label="Lädt"
          className="fixed inset-x-0 top-0 z-[80] h-0.5 animate-pulse bg-akzent"
        />
      )}
      <PageLayout>
        <Outlet />
      </PageLayout>
      <NewBestellungSheet
        open={sheetOpen}
        onClose={closeSheet}
        onGespeichert={(nr) => neueBestellungGespeichert(navigate as never, nr)}
      />
      <ReserviertBlatt />
    </TourProvider>
  );
}

export const Route = createRootRoute({
  validateSearch: (search: Record<string, unknown>): RootSearch => {
    const out: RootSearch = {};
    if (search.neu === 1 || search.neu === '1') out.neu = 1;
    // Das Reserviert-Blatt (02.11.2026): leer = offen ohne Frage.
    if (typeof search.reserviert === 'string') out.reserviert = search.reserviert;
    return out;
  },
  component: Shell
});
